import { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import {
  CreditCard,
  Download,
  FileCheck,
  FileText,
  Lock,
  Users,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import {
  dashboardGreeting,
  dashboardQuickActions,
  nextGreetingBoundaryDelay,
} from "@/utils/dashboardEntry";
import "./dashboardEntry.css";

export function DashboardWelcome({
  onExport,
  exporting,
}: {
  onExport: () => void;
  exporting: boolean;
}) {
  const { user } = useAuth();
  const [now, setNow] = useState(() => new Date());
  const headingId = useId();
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const refresh = () => {
      clearTimeout(timer);
      const date = new Date();
      setNow(date);
      timer = setTimeout(refresh, nextGreetingBoundaryDelay(date));
    };
    const visible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    refresh();
    document.addEventListener("visibilitychange", visible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", visible);
    };
  }, []);
  const icons = {
    "new-application": FileText,
    payments: CreditCard,
    "enrolled-students": Users,
    "visa-documents": FileCheck,
  };
  return (
    <section className="dashboard-entry" aria-labelledby={headingId}>
      <div className="dashboard-entry__header">
        <div className="dashboard-entry__greeting">
          <h1 id={headingId}>{dashboardGreeting(user, now)}</h1>
          <p>What would you like to do today?</p>
        </div>
        <button
          type="button"
          className="btn btn-outline-primary dashboard-entry__export"
          onClick={onExport}
          disabled={exporting}
        >
          <Download size={18} aria-hidden="true" />
          {exporting ? "Exporting…" : "Export Report"}
        </button>
      </div>
      <h2 className="dashboard-entry__title">Quick Actions</h2>
      <div className="dashboard-entry__actions">
        {dashboardQuickActions(user).map((action) => {
          const Icon = icons[action.id];
          const content = (
            <>
              <span className="dashboard-entry__icon">
                <Icon size={20} aria-hidden="true" />
              </span>
              <span className="dashboard-entry__label">
                {action.label}
                {action.restricted && <Lock size={14} aria-hidden="true" />}
              </span>
              <span className="dashboard-entry__description">
                {action.description}
              </span>
            </>
          );
          return action.to ? (
            <Link
              key={action.id}
              to={action.to}
              className="dashboard-entry__action"
            >
              {content}
            </Link>
          ) : (
            <div
              key={action.id}
              className="dashboard-entry__action dashboard-entry__action--restricted"
              aria-disabled="true"
            >
              {content}
            </div>
          );
        })}
      </div>
    </section>
  );
}
